import { useState, useEffect, useCallback } from 'react';
import {
  Key,
  Plus,
  Trash2,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  Terminal,
} from '@/components/icons';
import {
  listApiKeys,
  createApiKey,
  revokeApiKey,
  type ApiKeyMetadata,
  type CreatedApiKey,
} from '../../services/apiKeysService';

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function mcpConfigSnippet(key: string): string {
  return `{
  "mcpServers": {
    "stacksketch": {
      "command": "npx",
      "args": ["-y", "@stack-sketch/mcp"],
      "env": { "STACKSKETCH_API_KEY": "${key}" }
    }
  }
}`;
}

export function ApiKeysSection() {
  const [keys, setKeys] = useState<ApiKeyMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedConfig, setCopiedConfig] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setKeys(await listApiKeys());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || creating) return;
    setCreating(true);
    setError(null);
    try {
      const created = await createApiKey(newName.trim());
      setCreatedKey(created);
      setNewName('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const copy = async (text: string, which: 'key' | 'config') => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === 'key') {
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
      } else {
        setCopiedConfig(true);
        setTimeout(() => setCopiedConfig(false), 2000);
      }
    } catch {
      /* clipboard unavailable — user can select manually */
    }
  };

  const handleRevoke = async (id: string) => {
    if (
      !window.confirm(
        'Revoke this API key? Any integration using it (e.g. the MCP server) will immediately stop working.'
      )
    ) {
      return;
    }
    setRevoking(id);
    setError(null);
    try {
      await revokeApiKey(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke API key');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <Key className="w-5 h-5" />
          API Keys
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Personal keys for programmatic access — used by the StackSketch MCP
          server so AI clients (Claude Desktop, Claude Code, Cursor) can read and
          create diagrams in your account.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Newly created key — shown once */}
      {createdKey && (
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Check className="w-4 h-4 text-green-500" />
            Key created — copy it now, you won&apos;t be able to see it again.
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-lg bg-muted px-3 py-2 font-mono text-xs text-foreground">
              {createdKey.key}
            </code>
            <button
              onClick={() => copy(createdKey.key, 'key')}
              className="flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-xs font-medium text-background hover:opacity-90 transition-opacity"
            >
              {copiedKey ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copiedKey ? 'Copied' : 'Copy'}
            </button>
          </div>

          <div className="pt-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Terminal className="w-3.5 h-3.5" />
                Add to your MCP client config
              </span>
              <button
                onClick={() => copy(mcpConfigSnippet(createdKey.key), 'config')}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {copiedConfig ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedConfig ? 'Copied' : 'Copy config'}
              </button>
            </div>
            <pre className="overflow-x-auto rounded-lg bg-muted p-3 font-mono text-[11px] leading-relaxed text-foreground">
              {mcpConfigSnippet(createdKey.key)}
            </pre>
          </div>

          <button
            onClick={() => setCreatedKey(null)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Done
          </button>
        </div>
      )}

      {/* Create form */}
      <form onSubmit={handleCreate} className="flex items-end gap-2">
        <div className="flex-1">
          <label htmlFor="api-key-name" className="block text-xs font-medium text-muted-foreground mb-1">
            New key name
          </label>
          <input
            id="api-key-name"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Claude Desktop"
            maxLength={255}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <button
          type="submit"
          disabled={!newName.trim() || creating}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Create
        </button>
      </form>

      {/* Existing keys */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading keys…
          </div>
        ) : keys.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No API keys yet. Create one above to connect the MCP server.
          </p>
        ) : (
          keys.map((k) => (
            <div
              key={k.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-border bg-background px-4 py-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-foreground truncate">{k.name}</span>
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                    {k.keyPrefix}…
                  </code>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Created {formatDate(k.createdAt)} · Last used {formatDate(k.lastUsedAt)}
                  {k.expiresAt ? ` · Expires ${formatDate(k.expiresAt)}` : ''}
                </div>
              </div>
              <button
                onClick={() => handleRevoke(k.id)}
                disabled={revoking === k.id}
                className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50 transition-colors"
              >
                {revoking === k.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
                Revoke
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ApiKeysSection;
